import React, { useState } from 'react';
import { ChevronDown, ExternalLink, Loader2 } from 'lucide-react';
import CandidatePicker, { BirthCompare } from './CandidatePicker';
import SubjectNegative from './SubjectNegative';

const PEP = ['pep', 'public_functions', 'local_politicians'];
const STATUS = {
  hit: ['POSITIVO', 'text-[#E37A72] border-[#E37A72]/40 bg-[#E37A72]/[0.06]'],
  clear: ['NEGATIVO', 'text-[#5FBF8E] border-[#5FBF8E]/40 bg-[#5FBF8E]/[0.06]'],
  error: ['NON ESEGUITA', 'text-[#D9822B] border-[#D9822B]/40 bg-[#D9822B]/[0.06]'],
  skipped: ['N/A', 'text-[#5A5F67] border-[#1F242C]'],
};
export const KIND = { person: 'Persona fisica', company: 'Persona giuridica' };

const evidences = (sources, key) => (sources.sgr || []).flatMap((e) => (e.evidences || [])
  .filter((ev) => (key === 'pep' ? PEP.includes(ev.interest_area) : ev.interest_area === key)));

const Link = ({ href, children }) => (href ? <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#D4AF37] hover:underline">{children}<ExternalLink size={11} /></a> : null);

function Row({ title, sub, link, note }) {
  return (
    <div className="py-2.5 border-b border-[#1F242C] last:border-0 text-sm">
      <p className="text-[#E5E5E5]">{title}</p>
      {note && <p className="text-xs text-[#D4AF37] mt-0.5" data-testid="aml-source-note">{note}</p>}
      {sub && <p className="text-xs text-[#8A8F97] mt-0.5">{sub}</p>}
      {link && <p className="text-xs mt-1"><Link href={link}>Apri fonte</Link></p>}
    </div>
  );
}

function Details({ k, sources }) {
  if (['adverse_media', 'pep', 'sanctions', 'legal_enforcement'].includes(k)) {
    return evidences(sources, k).map((e, i) => (
      <Row key={i} title={e.role || e.subject || e.organization || 'Riscontro'} link={e.links?.[0]}
        sub={[e.source, e.publication_date || e.date_start, e.organization, e.organization_codes].filter(Boolean).join(' · ')} />));
  }
  if (k === 'pil') return (sources.pil || []).map((p, i) => <Row key={i} title={`${p.name} · ${p.birth_date} ${p.birth_place}`} link={p.url} sub={(p.roles || []).map((r) => `${r.role} ${r.entity} (${r.start}–${r.end || 'in corso'})`).join(' · ')} />);
  if (k === 'offshore') return (sources.offshore || []).map((o, i) => <Row key={i} title={`${o.name} · ${o.kind}`} sub={[o.details, o.source].filter(Boolean).join(' · ')} link={o.url} />);
  return (sources.italgiure || []).map((s, i) => <Row key={i} title={`${s.title} ${s.ecli || ''}`} note={s.note} sub={[s.section && `Sez. ${s.section}`, s.hearing && `udienza ${s.hearing}`, s.excerpt].filter(Boolean).join(' · ')} link={s.url} />);
}

function Category({ c, sources }) {
  const [open, setOpen] = useState(false);
  const [label, cls] = STATUS[c.status] || STATUS.skipped;
  const canOpen = c.status === 'hit';
  return (
    <div className="border-b border-[#1F242C] last:border-0" data-testid={`aml-cat-${c.key}`}>
      <button disabled={!canOpen} onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 py-3 text-left">
        <span className="flex-1 text-sm text-[#C9CCD1]">{c.label}{c.note && <span className="block text-xs text-[#5A5F67]">{c.note}</span>}</span>
        <span className={`text-[10px] font-mono tracking-wider px-2 py-1 border rounded-sm ${cls}`} data-testid={`aml-cat-${c.key}-status`}>{label}{c.status === 'hit' ? ` · ${c.count}` : ''}</span>
        {canOpen && <ChevronDown size={15} className={`text-[#5A5F67] transition-transform ${open ? 'rotate-180' : ''}`} />}
      </button>
      {open && <div className="pb-3 pl-3 border-l border-[#D4AF37]/40 ml-1"><Details k={c.key} sources={sources} /></div>}
    </div>
  );
}

export default function SubjectResult({ s, idx, checkId, onUpdate }) {
  const [repick, setRepick] = useState(false);
  const sources = s.result?.sources || {};
  const choosing = s.status === 'choose' || repick;
  const update = (doc) => { setRepick(false); onUpdate(doc); };
  const assoc = (sources.sgr || []).flatMap((e) => e.associated_entities || []);
  return (
    <div className="border border-[#1F242C] bg-[#0B0D10] p-5" data-testid={`aml-subject-${idx}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-base text-[#F2F2F2]">{s.name}</p>
          <p className="text-xs font-mono text-[#5A5F67]">{[KIND[s.kind], s.code, s.role].filter(Boolean).join(' · ')}</p>
          {s.matched && <p className="text-xs text-[#8A8F97] mt-1" data-testid={`aml-subject-${idx}-matched`}>Soggetto identificato: <span className="text-[#E5E5E5]">{s.matched.name}</span></p>}
          {s.selection === 'none' && <p className="text-xs text-[#E37A72] mt-1" data-testid={`aml-subject-${idx}-matched`}>Hai indicato che nessuno dei {s.candidates?.length} soggetti trovati corrisponde: i loro {(s.candidates || []).reduce((n, c) => n + (c.evidences_count || 0), 0)} riscontri sono esclusi dal risultato</p>}
        </div>
        {s.status === 'running'
          ? <span className="flex items-center gap-2 text-xs text-[#8A8F97]" data-testid={`aml-subject-${idx}-running`}><Loader2 size={14} className="animate-spin text-[#D4AF37]" />Verifica in corso…</span>
          : <span className="text-xs font-mono uppercase tracking-wider text-[#D4AF37]" data-testid={`aml-subject-${idx}-outcome`}>{s.outcome}</span>}
      </div>
      {!choosing && <BirthCompare s={s} idx={idx} />}
      {!choosing && s.status === 'done' && s.candidates?.length > 1 && (
        <button onClick={() => setRepick(true)} className="text-xs text-[#D4AF37] hover:underline mb-2" data-testid={`aml-repick-${idx}`}>Cambia soggetto ({s.candidates.length} trovati)</button>
      )}
      {choosing && <CandidatePicker s={s} idx={idx} checkId={checkId} onUpdate={update} onCancel={repick ? () => setRepick(false) : null} />}
      {!choosing && (s.summary || []).map((c) => <Category key={c.key} c={c} sources={sources} />)}
      {!choosing && s.negative_id && <SubjectNegative checkId={checkId} idx={idx} />}
      {!choosing && assoc.length > 0 && (
        <div className="mt-3"><p className="text-[11px] uppercase tracking-[0.15em] text-[#5A5F67] mb-1">Soggetti collegati · {assoc.length}</p>
          {assoc.slice(0, 20).map((a, i) => <Row key={i} title={a.name} sub={[a.relationship, a.details, a.entity_type].filter(Boolean).join(' · ')} />)}
        </div>
      )}
    </div>
  );
}
